import { type AnyAction, combineReducers } from 'redux';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';

import { AssetContainerTypes, LicenseEnum } from 'js/src/client';
import {
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
} from 'js/src/data/media';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import {
    addFiles,
    cancelAllUploads,
    clearFailedUploadMessages,
    clearUploads,
    createUploadSession,
    deleteUploadSession,
    endUploadExperience,
    finalizeAttributionData,
    markFileAsDeleted,
    markFileAsDeleting,
    markFileAsDeletionFailed,
    markFileAsFinalizing,
    removeUpload,
    setUploadFolder,
    startUploadExperience,
    storeCompletedFile,
    storeFailedUpload,
    storeSuccessfulUpload,
    storeUpdatedFileMetadata,
    storeUploadProgress,
    updateAttributionData,
    updateUploadedAssetMediaTags,
    updateUploadMediaTags,
    userCancelFile,
} from 'js/src/data/uploads/uploads-actions';
import type {
    FailedUploadMessage,
    FileUpload,
    StoredUploadedAsset,
    StoredUploadOptions,
    UploadSession,
} from 'js/src/data/uploads/uploads-types';
import {
    FILE_STATUS,
    immutableAssetContainerModelIsAudio,
    immutableAssetContainerModelIsBackground,
    immutableAssetContainerModelIsIcon,
} from 'js/src/data/uploads/uploads-types';
import uuid from 'js/src/libs/uuid';

type FilesState = PlainJsStoreOrmData<FileUpload, string>;
type UploadedAssetsState = PlainJsStoreOrmData<StoredUploadedAsset, string>;
type UploadSessionsState = Readonly<Record<string, UploadSession>>;
type ProgressState = Readonly<Record<string, number>>;

const EMPTY_FILES: FilesState = PlainJsStoreORM.createEmpty<FileUpload, string>().data;
const EMPTY_UPLOADED_ASSETS: UploadedAssetsState = PlainJsStoreORM.createEmpty<
    StoredUploadedAsset,
    string
>().data;
const EMPTY_UPLOAD_SESSIONS: UploadSessionsState = {};
const EMPTY_PROGRESS: ProgressState = {};
const EMPTY_MEDIA_TAGS: readonly string[] = [];
const EMPTY_FAIL_MESSAGES: readonly FailedUploadMessage[] = [];

function find_file_id(file_obj: File, orm: PlainJsStoreORM<FileUpload, string>): string | null {
    const results = orm.filter((file) => file.file_obj === file_obj);
    if (results.length === 1) {
        return results[0].id;
    }
    return null;
}

function withSessionChanges(
    session: UploadSession,
    changes: Partial<UploadSession>,
): UploadSession {
    const hasChanges = (Object.keys(changes) as (keyof UploadSession)[]).some(
        (key) => changes[key] !== session[key],
    );
    return hasChanges ? { ...session, ...changes } : session;
}

function withFileStatus(state: FilesState, fileId: string, status: FILE_STATUS): FilesState {
    const orm = new PlainJsStoreORM(state);
    if (!orm.contains(fileId)) {
        return state;
    }
    orm.updateById(fileId, (file) => (file.status === status ? file : { ...file, status }));
    return orm.data;
}

export function upload_sessions(
    state: UploadSessionsState = EMPTY_UPLOAD_SESSIONS,
    action: AnyAction,
): UploadSessionsState {
    if (!action.payload?.uploadSessionId) {
        return state;
    }

    if (createUploadSession.match(action)) {
        return {
            ...state,
            [action.payload.uploadSessionId]: {
                files: files(undefined, action),
                // passing state = undefined to get default values
                files_can_be_edited: files_can_be_edited(undefined, action),
                folderId: folderId(undefined, action),
                attribution: attribution(undefined, action),
                media_tags: media_tags(undefined, action),
                license: license(undefined, action),
            },
        };
    }
    if (deleteUploadSession.match(action)) {
        if (!(action.payload.uploadSessionId in state)) {
            return state;
        }
        const remainingSessions = { ...state };
        delete remainingSessions[action.payload.uploadSessionId];
        return remainingSessions;
    }
    const uploadSessionId: string = action.payload.uploadSessionId;
    const uploadSession = state[uploadSessionId];
    if (!uploadSession) {
        throw Error('attempting to change an upload session before it has been created');
    }
    const updatedSession = uploadSessionReducer(uploadSession, action);
    if (updatedSession === uploadSession) {
        return state;
    }
    return { ...state, [uploadSessionId]: updatedSession };
}

function uploadSessionReducer(uploadSession: UploadSession, action: AnyAction): UploadSession {
    if (startUploadExperience.match(action) || endUploadExperience.match(action)) {
        return withSessionChanges(uploadSession, {
            files_can_be_edited: files_can_be_edited(uploadSession.files_can_be_edited, action),
            folderId: folderId(uploadSession.folderId, action),
        });
    }
    if (
        cancelAllUploads.match(action) ||
        addFiles.match(action) ||
        storeSuccessfulUpload.match(action) ||
        updateUploadedAssetMediaTags.match(action) ||
        userCancelFile.match(action) ||
        markFileAsFinalizing.match(action) ||
        markFileAsDeleting.match(action) ||
        storeCompletedFile.match(action) ||
        storeUpdatedFileMetadata.match(action) ||
        markFileAsDeleted.match(action) ||
        markFileAsDeletionFailed.match(action) ||
        storeFailedUpload.match(action)
    ) {
        return withSessionChanges(uploadSession, {
            files: files(uploadSession.files, action),
        });
    }
    if (setUploadFolder.match(action)) {
        return withSessionChanges(uploadSession, {
            folderId: folderId(uploadSession.folderId, action),
        });
    }
    if (finalizeAttributionData.match(action) || updateAttributionData.match(action)) {
        return withSessionChanges(uploadSession, {
            attribution: attribution(uploadSession.attribution, action),
            license: license(uploadSession.license, action),
        });
    }
    if (updateUploadMediaTags.match(action)) {
        return withSessionChanges(uploadSession, {
            media_tags: media_tags(uploadSession.media_tags, action),
        });
    }
    return uploadSession;
}

export function files(state: FilesState = EMPTY_FILES, action: AnyAction): FilesState {
    if (cancelAllUploads.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.updateAll((file) => (file.user_canceled ? file : { ...file, user_canceled: true }));
        return orm.data;
    }
    if (addFiles.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const upload_opts: StoredUploadOptions = (({ file_list, ...rest }) => rest)(
            action.payload.opts,
        );
        action.payload.opts.file_list.forEach((file_obj) => {
            orm.pushItem(
                {
                    file_obj: file_obj.file,
                    file_metadata: { ...file_obj.metadata },
                    upload_opts,
                    status: FILE_STATUS.LOADING,
                    user_canceled: false,
                    uploaded_asset: null,
                    fail_message: '',
                    tags: EMPTY_MEDIA_TAGS,
                    id: uuid(),
                },
                'id',
            );
        });
        return orm.data;
    }
    if (storeSuccessfulUpload.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('storeSuccessfulUpload: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, (file) => ({
            ...file,
            status: FILE_STATUS.VALIDATED,
            uploaded_asset: action.payload.new_asset,
            asyncOperationUuid: action.payload.async_operation_uuid,
        }));
        return orm.data;
    }
    if (updateUploadedAssetMediaTags.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error(
                'updateUploadedAssetMediaTags: attempting to update a file that does not exist',
            );
        }
        orm.updateById(file_id, (file) => ({
            ...file,
            tags: uniq([...file.tags, ...action.payload.tags]),
        }));
        return orm.data;
    }
    if (userCancelFile.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('userCancelFile: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, (file) =>
            file.user_canceled ? file : { ...file, user_canceled: true },
        );
        return orm.data;
    }
    if (markFileAsFinalizing.match(action)) {
        return withFileStatus(state, action.payload.file_id, FILE_STATUS.FINALIZING);
    }
    if (markFileAsDeleting.match(action)) {
        return withFileStatus(state, action.payload.file_id, FILE_STATUS.DELETING);
    }
    if (storeCompletedFile.match(action)) {
        const orm = new PlainJsStoreORM(state);
        if (!orm.contains(action.payload.file_id)) {
            return state;
        }
        orm.updateById(action.payload.file_id, (file) => ({
            ...file,
            status: FILE_STATUS.COMPLETED,
            uploaded_asset: action.payload.new_asset,
        }));
        return orm.data;
    }
    if (storeUpdatedFileMetadata.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const file_id = find_file_id(action.payload.file_with_metadata.file, orm);
        if (!file_id) {
            throw Error(
                'storeUpdatedFileMetadata: attempting to update a file that does not exist',
            );
        }
        orm.updateById(file_id, (file) => ({
            ...file,
            file_metadata: {
                ...file.file_metadata,
                ...action.payload.file_with_metadata.metadata,
                ...action.payload.new_metadata,
            },
        }));
        return orm.data;
    }
    if (markFileAsDeleted.match(action)) {
        return withFileStatus(state, action.payload.file_id, FILE_STATUS.DELETED);
    }
    if (markFileAsDeletionFailed.match(action)) {
        return withFileStatus(state, action.payload.file_id, FILE_STATUS.DELETE_FAILED);
    }
    if (storeFailedUpload.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const file_id = find_file_id(action.payload.file_obj, orm);
        if (!file_id) {
            throw Error('storeFailedUpload: attempting to update a file that does not exist');
        }
        orm.updateById(file_id, (file) =>
            file.status === FILE_STATUS.FAILED && file.fail_message === action.payload.message
                ? file
                : { ...file, status: FILE_STATUS.FAILED, fail_message: action.payload.message },
        );
        return orm.data;
    }
    return state;
}

export function files_can_be_edited(state = false, action: AnyAction) {
    if (startUploadExperience.match(action)) {
        return true;
    }
    if (endUploadExperience.match(action)) {
        return false;
    }
    return state;
}

export function progress(state: ProgressState = EMPTY_PROGRESS, action: AnyAction): ProgressState {
    if (storeUploadProgress.match(action)) {
        const { session_id } = action.payload;
        if (action.payload.progress < 0) {
            if (!(session_id in state)) {
                return state;
            }
            const remainingProgress = { ...state };
            delete remainingProgress[session_id];
            return remainingProgress;
        }
        if (state[session_id] === action.payload.progress) {
            return state;
        }
        return { ...state, [session_id]: action.payload.progress };
    }
    return state;
}

export function folderId(state: number | null = null, action: AnyAction) {
    if (startUploadExperience.match(action)) {
        return null;
    }
    if (endUploadExperience.match(action) || setUploadFolder.match(action)) {
        return action.payload.folderId;
    }
    return state;
}

export function attribution(state = '', action: AnyAction) {
    if (finalizeAttributionData.match(action) || updateAttributionData.match(action)) {
        return action.payload.attribution;
    }
    return state;
}

export function media_tags(state: readonly string[] = EMPTY_MEDIA_TAGS, action: AnyAction) {
    if (updateUploadMediaTags.match(action)) {
        const { mediaTags } = action.payload;
        if (isEqual(state, mediaTags)) {
            return state;
        }
        return mediaTags.length === 0 ? EMPTY_MEDIA_TAGS : mediaTags;
    }
    return state;
}

export function license(state = LicenseEnum.USER_PRIVATE, action: AnyAction) {
    if (finalizeAttributionData.match(action)) {
        return action.payload.license;
    }
    return state;
}

function uploadedAssetMatchesRemovedAsset(
    removedAsset: ReturnType<typeof removeUpload>['payload']['asset'],
): (uploadedAsset: StoredUploadedAsset) => boolean {
    if (immutableAssetContainerModelIsBackground(removedAsset)) {
        const background = removedAsset.get(AssetContainerTypes.BACKGROUND);
        if (immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(background)) {
            const uuid = background.get('video').get('uuid');
            return (uploadedAsset) =>
                uploadedAsset.type === AssetContainerTypes.BACKGROUND &&
                uploadedAsset.asset.video?.uuid === uuid;
        }
        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(background)) {
            const uuid = background.get('image').get('uuid');
            return (uploadedAsset) =>
                uploadedAsset.type === AssetContainerTypes.BACKGROUND &&
                uploadedAsset.asset.image?.uuid === uuid;
        }
        throw new Error('removeUpload: attempting to remove a background that is not supported');
    }
    if (immutableAssetContainerModelIsAudio(removedAsset)) {
        const uuid = removedAsset.get(AssetContainerTypes.AUDIO).get('uuid');
        return (uploadedAsset) =>
            uploadedAsset.type !== AssetContainerTypes.BACKGROUND &&
            uploadedAsset.asset.uuid === uuid;
    }
    if (immutableAssetContainerModelIsIcon(removedAsset)) {
        const uuid = removedAsset.get(AssetContainerTypes.ICON).get('uuid');
        return (uploadedAsset) =>
            uploadedAsset.type !== AssetContainerTypes.BACKGROUND &&
            uploadedAsset.asset.uuid === uuid;
    }
    throw Error('removeUpload: attempting to remove an asset that is not supported');
}

export function uploaded_assets(
    state: UploadedAssetsState = EMPTY_UPLOADED_ASSETS,
    action: AnyAction,
): UploadedAssetsState {
    if (storeCompletedFile.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const { new_asset, new_asset_type } = action.payload;
        // font and document uploads should never call this action
        if (
            new_asset_type === AssetContainerTypes.FONT ||
            new_asset_type === AssetContainerTypes.DOCUMENT
        ) {
            throw Error('storeCompletedFile: attempting to store a font or document upload');
        }
        orm.pushItem(
            {
                id: uuid(),
                type: new_asset_type,
                asset: new_asset,
            } as StoredUploadedAsset,
            'id',
        );
        return orm.data;
    }
    if (removeUpload.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const matchingUploads = orm.filter(uploadedAssetMatchesRemovedAsset(action.payload.asset));
        if (matchingUploads.length === 1) {
            orm.delete([matchingUploads[0].id]);
        }
        return orm.data;
    }
    if (clearUploads.match(action)) {
        return EMPTY_UPLOADED_ASSETS;
    }
    return state;
}

export function fail_messages(
    state: readonly FailedUploadMessage[] = EMPTY_FAIL_MESSAGES,
    action: AnyAction,
): readonly FailedUploadMessage[] {
    if (storeFailedUpload.match(action)) {
        return [
            ...state,
            {
                name: action.payload.file_obj.name,
                message: action.payload.message,
            },
        ];
    }
    if (clearFailedUploadMessages.match(action)) {
        return EMPTY_FAIL_MESSAGES;
    }
    return state;
}

export const uploads = combineReducers({
    upload_sessions,
    fail_messages,
    progress,
    uploaded_assets,
});

export type UploadsState = ReturnType<typeof uploads>;
